import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { useCountdown } from 'usehooks-ts';
import { Modal } from 'components/Modal';
import { ConfirmModal } from 'components/Shared/ConfirmModal';
import { useHistory } from 'hooks/generic/useHistory';
import { useSearchParams } from 'hooks/useSearchParams';
import * as Styled from './styles';
import {
  customerEmailUnsubscribe,
  NewsLetterEmailsOtp
} from 'appRedux/restApi';
import { getPath, isMobile } from 'utils';
import passwordChangeSuccess from 'assets/images/success-tick.png';

export const NewsLetterEmailUnsubscibe = () => {
  const searchParams = useSearchParams();
  const { history } = useHistory();
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [otpSubmitDisabled, setOtpSubmitDisabled] = useState(true);

  const COUNT_START = 60;
  const INTERVAL = 1000;
  const [count, { startCountdown, resetCountdown }] = useCountdown({
    countStart: COUNT_START,
    intervalMs: INTERVAL
  });
  const [otpError, setOtpError] = useState({
    isInvalid: false,
    text: ''
  });
  const [code, setCode] = useState<string>('');

  const sendResendOtp = () => {
    const { email } = searchParams;
    if (email) {
      NewsLetterEmailsOtp({ email })
        .then(data => {
          if (data.error) {
            message.error(t('account.unsubscribedError'));
          } else {
            resetCountdown();
            startCountdown();
            setIsOpen(true);
          }
        })
        .catch(() => {
          message.error(t('account.unsubscribedError'));
        });
    }
  };

  const INPUT_STYLE = {
    backgroundColor: 'var(--v3-black-2)',
    MozAppearance: 'textfield' as const,
    color: 'var(--v3-text-color)',
    borderRadius: '20px',
    border: 'none',
    textAlign: 'center' as const,
    fontSize: isMobile() ? '28px' : '32px',
    height: isMobile() ? '80px' : '96px',
    margin: '0 0 20px 0',
    width: '16%'
  };

  const INPUT_STYLE_INVALID = {
    ...INPUT_STYLE,
    border: '1px solid var(--v3-error-color)'
  };

  const handleRedirect = () => {
    history.replace(getPath('/'));
  };

  const handleSubmit = () => {
    if (otpSubmitDisabled) return;
    const { email } = searchParams;
    const token = code;
    const type = 'news_letter';
    if (email && token) {
      customerEmailUnsubscribe({ email, token, type })
        .then(data => {
          if (data.error_code) {
            setOtpError({
              isInvalid: true,
              text: t('error.WrongOtpError')
            });
            setIsOpen(true);
          } else {
            setVisible(true);
            setIsOpen(false);
          }
        })
        .catch(() => {
          message.error(t('account.unsubscribedError'));
          handleRedirect();
        });
    } else {
      handleRedirect();
    }
  };

  const onCloseModal = () => {
    setIsOpen(false);
    handleRedirect();
  };

  const ModalBody = (
    <Styled.ModalWrapper>
      <Styled.Title>{t('account.unsubscribedModalTitle')}</Styled.Title>
      <Styled.BottomWrapper>
        <Styled.Description>
          {t('account.unsubscribedModalDescription')}
        </Styled.Description>
      </Styled.BottomWrapper>
      <Styled.Border></Styled.Border>
      <Styled.OtpWrapper>
        <Styled.OtpWrapperHeading>
          {t('account.unsubscribedModalInfo')}
        </Styled.OtpWrapperHeading>
        <Styled.ReactCodeInputWrapper>
          <Styled.ReactCodeInput
            name="pin"
            type="number"
            fields={6}
            inputStyle={otpError.isInvalid ? INPUT_STYLE_INVALID : INPUT_STYLE}
            inputMode="numeric"
            onChange={setCode}
          />
        </Styled.ReactCodeInputWrapper>
        {otpError.isInvalid && (
          <Styled.TerminalVerifyWithOtpCodeError>
            {otpError.text}
          </Styled.TerminalVerifyWithOtpCodeError>
        )}
        {!!count && (
          <Styled.ResendHint>
            <Styled.Counter
              dangerouslySetInnerHTML={{
                __html: t('account.unsubscribeModalResendCountdown', {
                  count
                })
              }}
            />
          </Styled.ResendHint>
        )}
      </Styled.OtpWrapper>
      <Styled.ButtonWrapper>
        <Styled.ButtonContainer>
          <Styled.ConfirmationButton
            type="secondary"
            disabled={!!count}
            btnheight={56}
            onClick={sendResendOtp}
          >
            {t('account.resend')}
          </Styled.ConfirmationButton>
        </Styled.ButtonContainer>

        <Styled.ButtonContainer>
          <Styled.ConfirmationButton
            type="primary"
            onClick={handleSubmit}
            btnheight={56}
            disabled={otpSubmitDisabled}
          >
            {t('account.submit')}
          </Styled.ConfirmationButton>
        </Styled.ButtonContainer>
      </Styled.ButtonWrapper>
    </Styled.ModalWrapper>
  );

  useEffect(() => {
    resetCountdown();
    startCountdown();
    sendResendOtp();
  }, []);

  useEffect(() => {
    setOtpError({
      isInvalid: false,
      text: ''
    });
    if (code.length !== 6) {
      setOtpSubmitDisabled(true);
      return;
    }
    setOtpSubmitDisabled(false);
  }, [code]);

  return (
    <>
      {isOpen && (
        <Modal
          isOpen={isOpen}
          closeIconSize="small"
          onClose={onCloseModal}
          fullwidth={false}
        >
          {ModalBody}
        </Modal>
      )}
      (
      <ConfirmModal
        title={t('account.successfullyUnsubscribed')}
        visible={visible}
        onCancel={handleRedirect}
        image={passwordChangeSuccess}
        showConfirmButton={false}
      />
      )
    </>
  );
};
